import type { TFromFieldInfo_New } from '@verifime/components';
import {
  EXPIRED_DOCUMENT_BLOCKED_MESSAGE,
  getLenientToday,
  parseDateString,
  TIdentityDocumentType,
  validationRules,
} from '@verifime/utils';

/**
 * The expiry field of each document type the wizard captures one for (VME-4254). The Other
 * identity document has no entry: the wizard captures no expiry for it, so it cannot be blocked
 * here; person-api refuses it when an admin enters one.
 */
export const EXPIRY_FIELD_BY_DOCUMENT_TYPE: Partial<Record<TIdentityDocumentType, string>> = {
  Passport: 'passportExpiry',
  DriverLicence: 'licenceExpiry',
  MedicareCard: 'medicareCardExpiry',
  PhotoCard: 'photoCardExpiry',
  ImmiCard: 'immiCardExpiry',
};

const MEDICARE_CARD_COLOUR_FIELD = 'medicareCardColour';
const MEDICARE_CARD_GREEN = 'Green';

const monthIndexOf = (date: Date) => date.getFullYear() * 12 + date.getMonth();

type TExpiryCheck = {
  documentType: TIdentityDocumentType;
  /** A `yyyy-MM-dd` string, or a Date the form has already parsed. */
  expiry: string | Date | null | undefined;
  medicareCardColour?: string | null;
  now: Date;
};

export type TExpiryStatus = 'expired' | 'current' | 'unknown';

/**
 * Where the expiry stands against the lenient today (person-api's Floor rounding: the calendar
 * date at UTC-18), so a document that expires earlier today anywhere on Earth is not yet expired.
 *
 * A Green Medicare card is valid through the end of its month, so it compares year and month
 * only. `unknown` is a blank expiry, a value that is not a date (a half-typed one, say), and a
 * type the wizard captures no expiry for: they are not this check's concern, and the field's own
 * rules decide.
 */
export function expiryStatusOf({
  documentType,
  expiry,
  medicareCardColour,
  now,
}: TExpiryCheck): TExpiryStatus {
  if (!EXPIRY_FIELD_BY_DOCUMENT_TYPE[documentType] || !expiry) {
    return 'unknown';
  }
  const expiryDate = parseDateString('', expiry as string);
  if (!(expiryDate instanceof Date) || Number.isNaN(expiryDate.getTime())) {
    return 'unknown';
  }

  const lenientToday = getLenientToday(now);
  const isMonthPrecision =
    documentType === 'MedicareCard' && medicareCardColour === MEDICARE_CARD_GREEN;
  const isExpired = isMonthPrecision
    ? monthIndexOf(expiryDate) < monthIndexOf(lenientToday)
    : expiryDate.getTime() < lenientToday.getTime();
  return isExpired ? 'expired' : 'current';
}

/** Whether the expiry is before the lenient today; see `expiryStatusOf`. */
export function isIdentityDocumentExpired(check: TExpiryCheck): boolean {
  return expiryStatusOf(check) === 'expired';
}

/**
 * Where the expiry stands for the fields a form holds, given whether expired documents are
 * accepted. A document that is accepted, or of a type the wizard captures no expiry for, is
 * `current`: there is nothing to block. The New Relic report reads this.
 */
export function expiryStatusOfDocumentFields(
  documentType: TIdentityDocumentType,
  documentFields: Record<string, string | Date> | undefined,
  expiredDocumentAccepted: boolean,
  now: Date,
): TExpiryStatus {
  const expiryField = EXPIRY_FIELD_BY_DOCUMENT_TYPE[documentType];
  if (expiredDocumentAccepted || !expiryField) {
    return 'current';
  }
  return expiryStatusOf({
    documentType,
    expiry: documentFields?.[expiryField],
    medicareCardColour: documentFields?.[MEDICARE_CARD_COLOUR_FIELD] as string | undefined,
    now,
  });
}

/**
 * The fields of one document type with a wizard-local rule on its expiry field: the expiry must
 * not be before the lenient today. Used when no organisation of the customer accepts an expired
 * document of this type. Returns the same object when no rule applies (the type has no expiry
 * captured, or expired documents are accepted).
 *
 * The shared expiry rules in `@verifime/utils` are the base and are left unchanged, because the
 * admin editors use them. A new field map is returned rather than the shared one changed, since
 * the Medicare card's fields are a module constant.
 *
 * `getNow` is read each time the form is checked, never when the rule is built, so a form left
 * open past midnight is judged against the day it is on.
 */
export function withExpiredDocumentRule(
  documentType: TIdentityDocumentType,
  fields: Record<string, TFromFieldInfo_New>,
  expiredDocumentAccepted: boolean,
  getNow: () => Date = () => new Date(),
): Record<string, TFromFieldInfo_New> {
  const expiryField = EXPIRY_FIELD_BY_DOCUMENT_TYPE[documentType];
  if (expiredDocumentAccepted || !expiryField || !fields[expiryField]) {
    return fields;
  }

  const rejectsExpired = validationRules.REQUIRED_EXPIRY_DATE_VALIDATION_RULES.test(
    'is-not-expired',
    EXPIRED_DOCUMENT_BLOCKED_MESSAGE,
    function isNotExpired(value) {
      return !isIdentityDocumentExpired({
        documentType,
        expiry: value,
        medicareCardColour: this.parent?.[MEDICARE_CARD_COLOUR_FIELD],
        now: getNow(),
      });
    },
  );

  // A photo card can print no expiry, so a blank one passes with no check; anything typed must
  // still be a real date, as the optional rule it replaces says.
  const rule =
    documentType === 'PhotoCard' ? validationRules.optionalRule(rejectsExpired) : rejectsExpired;

  return { ...fields, [expiryField]: { ...fields[expiryField], rules: rule } };
}
